import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { AGENTS, useErp } from "@/context/ErpContext";
import { ArrowLeft, Sparkle, Check } from "@phosphor-icons/react";

// Group agents by category for a cleaner UI
const CATEGORIES = [
  {
    label: "Accounts Payable",
    keys: [
      "invoice-create",
      "invoice-ageing",
    ],
  },

  {
    label: "Accounts Receivable",
    keys: [
      "customer-create",
      "receipt-create",
    ],
  },

  {
    label: "Procurement",
    keys: [
      "supplier-create",
      "supplier-ageing",
      "purchase-order-create",
      "purchase-req-create",
      "rfp-create",
      "item-creation",
    ],
  },

  {
    label: "General Ledger",
    keys: [
      "journal-create",
    ],
  },

  {
    label: "Human Resources",
    keys: [
      "create-hr",
      "get-hr",
      "employee-leave-application",
    ],
  },

  {
    label: "Treasury",
    keys: [
      "bank-transfer",
      "bank-balance",
    ],
  },

  {
    label: "Asset Management",
    keys: [
      "asset-addition",
      "asset-transfer",
      "asset-retirement",
    ],
  },

  {
    label: "Order Management",
    keys: [
      "sales-order-booking",
    ],
  },
  {
    label: "Inventory Management",
    keys: [
      "move-order-create",
      "transact-move-order"
    ],
  },
  {
    label: "Manufacturing",
    keys: [
      "manufacturing-work-order",
    ],
  },
   {
    label: "Maintenance",
    keys: [
      "maintenance-work-order",
    ],
  },
  {
    label: "Finance",
    keys: [
      "finance-account-overview",
      "finance-anomaly",
      "finance-variance",
      "finance-future-insight",
      "finance-narrative",
      "finance-monthend",
    ],
  },
];

// Map key → label quickly
const agentMeta = Object.fromEntries(AGENTS.map((a) => [a.key, a]));

export default function UserAgentsPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { fetchCatalog } = useErp();

  const [user, setUser]         = useState(null);
  const [assigned, setAssigned] = useState(new Set()); // set of agent_keys
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [err, setErr]           = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [uRes, aRes] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get(`/users/${userId}/agents`),
        ]);
        setUser(uRes.data);
        // aRes.data might be [{ agent_key: "..." }] or ["..."]
        const keys = (aRes.data || []).map((a) =>
          typeof a === "string" ? a : a.agent_key
        );
        setAssigned(new Set(keys));
      } catch {
        setErr("Could not load user data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const toggle = (key) => {
    setAssigned((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setSaved(false);
  };

  const toggleAll = () => {
    const allKeys = AGENTS.map((a) => a.key);
    const allAssigned = allKeys.every((k) => assigned.has(k));
    setAssigned(allAssigned ? new Set() : new Set(allKeys));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      await api.post(`/users/${userId}/agents`, {
        agents: Array.from(assigned),
      });
      await fetchCatalog();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to save agent assignments.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = AGENTS.every((a) => assigned.has(a.key));
  const noneSelected = assigned.size === 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xs tracking-[0.2em] uppercase font-bold text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Admin · User Management"
        title={`Agent Access — ${user?.name || "User"}`}
        subtitle={`${user?.email} · ${user?.department || "No department"} · Choose which ERP AI agents this user can access.`}
        testid="user-agents-title"
        actions={
          <div className="flex items-center gap-3">
            <Link to="/admin/users" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2">
              <ArrowLeft size={13} weight="bold" /> Back to Users
            </Link>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-agents-btn"
              className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? "Saving…" : saved ? <><Check size={13} weight="bold" /> Saved</> : "Save changes"}
            </button>
          </div>
        }
      />

      {err && (
        <div className="mx-8 mt-4 text-xs text-[#DC2626] border border-[#DC2626] bg-red-50 px-4 py-3" data-testid="agents-error">
          {err}
        </div>
      )}

      <div className="p-8 space-y-6">
        {/* Select all / none quick controls */}
        <div className="flex items-center gap-4 border border-gray-200 bg-gray-50 px-5 py-3">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-600">
            {assigned.size} of {AGENTS.length} agents assigned
          </div>
          <div className="flex gap-3 ml-auto">
            <button
              onClick={toggleAll}
              data-testid="toggle-all-agents"
              className="text-xs font-bold text-[#0020C2] hover:underline uppercase tracking-wider"
            >
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          </div>
        </div>

        {/* Agents grouped by category */}
        {CATEGORIES.map(({ label, keys }) => (
          <div key={label} className="border border-gray-200 bg-white">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
              <Sparkle size={14} weight="fill" className="text-[#0020C2]" />
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {keys.map((key) => {
                const meta = agentMeta[key];
                if (!meta) return null;
                const isOn = assigned.has(key);
                return (
                  <label
                    key={key}
                    htmlFor={`agent-${key}`}
                    className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    data-testid={`agent-toggle-${key}`}
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{meta.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{meta.hint}</div>
                    </div>
                    {/* Custom toggle switch */}
                    <div className="relative ml-6 shrink-0">
                      <input
                        id={`agent-${key}`}
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(key)}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${isOn ? "bg-[#0020C2]" : "bg-gray-200"}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isOn ? "translate-x-5" : "translate-x-0"}`} />
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {/* Save button — bottom sticky bar for convenience */}
        <div className="sticky bottom-6">
          <div className="flex justify-end gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#0020C2] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 shadow-lg inline-flex items-center gap-2"
            >
              {saving ? "Saving…" : saved ? <><Check size={14} weight="bold" /> Saved</> : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}